import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useIndicator, useIndicatorReleases } from "../../hooks/useApi";
import { ImpactBadge, SurpriseBadge } from "../../components/Badge";
import { LoadingSpinner, ErrorMessage } from "../../components/LoadingSpinner";

export function IndicatorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);

  const indicator = useIndicator(id ?? "");
  const releases = useIndicatorReleases(id ?? "", page);

  if (!id) return <ErrorMessage message="指標IDが指定されていません" />;
  if (indicator.isLoading) return <LoadingSpinner />;
  if (indicator.isError) return <ErrorMessage message="指標の取得に失敗しました" />;
  if (!indicator.data) return null;

  const ind = indicator.data;
  const items = releases.data?.items ?? [];

  const chartData = [...items]
    .reverse()
    .map((r) => ({
      date: format(parseISO(r.released_at), "M/d"),
      result: r.result,
      forecast: r.forecast,
    }));

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/releases"
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3"
        >
          <ArrowLeft size={14} /> 発表履歴へ戻る
        </Link>
        <div className="flex items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{ind.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <span className="font-bold text-gray-700">{ind.country}</span>
              <span>·</span>
              <span>{ind.category}</span>
              <span>·</span>
              <span>{ind.frequency}</span>
              <span>·</span>
              <span>{ind.unit}</span>
            </div>
            {ind.description && (
              <p className="text-sm text-gray-500 mt-1">{ind.description}</p>
            )}
          </div>
        </div>
      </div>

      {releases.isLoading && <LoadingSpinner />}
      {releases.isError && <ErrorMessage message="発表履歴の取得に失敗しました" />}

      {chartData.length > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">時系列チャート</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(v: unknown) => [`${v} ${ind.unit}`, ""]}
              />
              <Line
                type="monotone"
                dataKey="result"
                stroke="#2563eb"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="実績"
              />
              <Line
                type="monotone"
                dataKey="forecast"
                stroke="#94a3b8"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                name="予想"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">発表履歴</h2>
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {items.length === 0 && !releases.isLoading && (
            <p className="py-8 text-center text-gray-500 text-sm">発表データなし</p>
          )}
          {items.map((r) => (
            <Link
              key={r.id}
              to={`/releases/${r.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xs text-gray-400 w-24 shrink-0">
                {format(parseISO(r.released_at), "yyyy/M/d HH:mm")}
              </span>
              <div className="flex-1">
                <span className="font-semibold text-gray-900">
                  {r.result}
                  <span className="text-xs text-gray-400 font-normal ml-0.5">{ind.unit}</span>
                </span>
                {r.forecast != null && (
                  <span className="text-xs text-gray-500 ml-2">予想 {r.forecast}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {r.surprise_direction && (
                  <SurpriseBadge direction={r.surprise_direction} />
                )}
                {r.analysis && <ImpactBadge level={r.analysis.impact_level} />}
              </div>
            </Link>
          ))}
        </div>
        {releases.data && releases.data.pages > 1 && (
          <div className="flex items-center justify-center gap-3 py-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-sm disabled:opacity-40"
            >
              <ChevronLeft size={14} /> 前
            </button>
            <span className="text-sm text-gray-600">
              {page} / {releases.data.pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(releases.data!.pages, p + 1))}
              disabled={page >= releases.data.pages}
              className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-sm disabled:opacity-40"
            >
              次 <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
